import FontAwesome from '@expo/vector-icons/FontAwesome'
import React, { useEffect, useRef } from 'react'
import { Animated, Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Footer from '@/components/Footer'
import { Icon } from '@/components/ui/Icon'
import { useThemeColors } from '@/contexts/ThemeContext'

const MAX_STAGGER_SLOTS = 10
const CONTENT_MAX_WIDTH = 640

type Props = {
  progress: number // 0–1
  onBack?: () => void
  bg?: string
  children: React.ReactNode
}

export function OnboardingShell({ progress, onBack, bg, children }: Props) {
  const colors = useThemeColors()
  const insets = useSafeAreaInsets()
  const { width } = useWindowDimensions()
  const isLarge = width >= 768
  const canUseNativeDriver = Platform.OS !== 'web'

  // Runway progress bar animation
  const progressAnim = useRef(new Animated.Value(progress)).current
  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: progress,
      duration: 350,
      useNativeDriver: false,
    }).start()
  }, [progress, progressAnim])

  // Staggered entrance animations — pre-allocate slots
  const anims = useRef<Animated.Value[]>(
    Array.from({ length: MAX_STAGGER_SLOTS }, () => new Animated.Value(0))
  ).current

  const childArray = React.Children.toArray(children)

  useEffect(() => {
    Animated.stagger(
      60,
      anims.slice(0, childArray.length).map((anim) =>
        Animated.spring(anim, {
          toValue: 1,
          useNativeDriver: canUseNativeDriver,
          damping: 20,
          stiffness: 120,
        })
      )
    ).start()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Responsive centering container style
  const centeredStyle = isLarge
    ? ({
        maxWidth: CONTENT_MAX_WIDTH,
        width: '100%',
        alignSelf: 'center' as const,
      } as const)
    : undefined

  return (
    <View style={{ flex: 1, backgroundColor: bg ?? colors.background }}>
      {/* Watermark airplane — decorative, non-interactive */}
      <View
        style={{
          position: 'absolute',
          bottom: -20,
          right: -30,
          opacity: 0.07,
          pointerEvents: 'none',
        }}
      >
        <FontAwesome name="plane" size={isLarge ? 520 : 320} color={colors.primary} />
      </View>

      {/* RunwayProgress — full-width 2px animated strip */}
      <View style={{ paddingTop: insets.top }}>
        <View style={{ height: 2, backgroundColor: colors.border }}>
          <Animated.View
            style={{
              height: 2,
              backgroundColor: colors.primary,
              width: progressAnim.interpolate({
                inputRange: [0, 1],
                outputRange: ['0%', '100%'],
              }),
            }}
          />
        </View>
      </View>

      {/* Scrollable area spans full width so the footer is not constrained by the
          centred column. Only the content children are wrapped in centeredStyle. */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'flex-start',
          paddingBottom: insets.bottom + 16,
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {/* Centred column: content area.
            paddingTop pushes content below the floating Header (insets.top already
            consumed by the progress bar above; 64 covers the header row height). */}
        <View style={[{ paddingTop: isLarge ? 80 : 20 }, centeredStyle]}>
          {childArray.map((child, i) => (
            <Animated.View
              key={i}
              style={{
                opacity: anims[i] ?? 1,
                transform: [
                  {
                    translateY:
                      anims[i]?.interpolate({
                        inputRange: [0, 1],
                        outputRange: [20, 0],
                      }) ?? 0,
                  },
                ],
              }}
            >
              {child}
            </Animated.View>
          ))}

          {/* Back button — below the answer options */}
          {onBack ? (
            <Pressable
              onPress={onBack}
              hitSlop={16}
              style={{ alignItems: 'center', paddingVertical: 20 }}
            >
              <Icon name="arrow-left" size={18} color={colors.muted} />
            </Pressable>
          ) : null}
        </View>

        {/* Footer — outside the centred column so it spans full screen width */}
        <View style={{ marginTop: 'auto', width: '100%' }}>
          <Footer />
        </View>
      </ScrollView>
    </View>
  )
}
